import Hero from "../components/Hero";
import SectionHeader from "../components/SectionHeader";
import ServiceCard from "../components/ServiceCard";
import ReviewCard from "../components/ReviewCard";
import CTASection from "../components/CTASection";
import { SERVICES, REVIEWS, STEPS, WHY_CHOOSE, GALLERY_ITEMS, ABOUT, ABOUT_IMAGE } from "../lib/business";
import { placeholderSvg } from "../lib/placeholder";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, Sparkles, Wallet, Hand, MapPin, CalendarCheck2, Smile, HeartHandshake, MessageSquare } from "lucide-react";

const WHY_ICONS = [Clock, Wallet, ShieldCheck, Sparkles, Hand, Smile, CalendarCheck2, MapPin, HeartHandshake, MessageSquare];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />

      {/* About */}
      <section className="section-y" data-testid="about-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <SectionHeader eyebrow="About" title={ABOUT.title} />
            <div className="mt-5 space-y-4 text-base text-muted-foreground leading-relaxed max-w-2xl">
              {ABOUT.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-2xl bg-secondary aspect-[4/3]">
              <img
                src={ABOUT_IMAGE}
                alt="Handyman installing a shelf in a modern home"
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderSvg("Abid Home Solutions"); }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="section-y" data-testid="services-preview">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <SectionHeader
              eyebrow="What We Do"
              title="Reliable handyman services done right."
              subtitle="From TV mounting to moving help — friendly, professional, and on time."
            />
            <Link to="/services" data-testid="see-all-services-link" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-accent">
              See all services <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.filter((s) => s.homePreview).map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="section-y bg-secondary/60" data-testid="why-choose-us">
        <div className="container-x">
          <SectionHeader
            eyebrow="Why Abid Home Solutions"
            title="Why Choose Abid Home Solutions"
            subtitle="A local home services business focused on reliable, respectful, high-quality work."
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  className="bg-white border border-border rounded-2xl p-6 card-elevate"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-accent">
                    <Icon size={18} />
                  </span>
                  <h3 className="mt-4 font-bold text-primary">{w.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works preview */}
      <section className="section-y" data-testid="how-it-works-preview">
        <div className="container-x">
          <SectionHeader
            eyebrow="How It Works"
            title="Three simple steps to get the job done."
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((step) => (
              <div key={step.n} data-testid={`step-card-${step.n}`} className="bg-white border border-border rounded-2xl p-7 card-elevate">
                <span className="text-5xl font-extrabold text-accent/40 tracking-tight">{step.n}</span>
                <h3 className="mt-3 text-xl font-bold text-primary">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/how-it-works" data-testid="see-how-link" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-accent">
              Learn more about our process <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews preview */}
      <section className="section-y bg-secondary/60" data-testid="reviews-preview">
        <div className="container-x">
          <SectionHeader
            eyebrow="Customer Reviews"
            title="5-star reviews from local customers."
            subtitle="From customers who booked us through Thumbtack and other local service platforms."
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {REVIEWS.slice(0, 2).map((r, i) => (
              <ReviewCard key={i} review={r} index={i} />
            ))}
          </div>
          <div className="mt-8">
            <Link to="/reviews" data-testid="see-all-reviews-link" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:text-accent">
              Read all reviews <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      <section className="section-y" data-testid="gallery-preview">
        <div className="container-x">
          <SectionHeader
            eyebrow="Our Work"
            title="Recent Projects"
            subtitle="Real project photos from local handyman and home service jobs."
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {GALLERY_ITEMS.slice(0, 6).map((g, i) => (
              <div key={i} data-testid={`gallery-preview-${i}`} className="group relative aspect-square overflow-hidden rounded-2xl bg-secondary">
                <img src={g.image} alt={g.label} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderSvg(g.category); }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/0 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="absolute bottom-3 left-3 right-3 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
